import { DOC_TYPES } from "../types";
import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";

type Props = Pick<
  KbAdminEditorSectionProps,
  | "aiGuidance"
  | "aiFeedback"
  | "aiPurpose"
  | "setAiPurpose"
  | "dictation"
  | "showAiFeedback"
  | "applyDictationToPurpose"
  | "aiSourceKind"
  | "setAiSourceKind"
  | "aiAudienceHint"
  | "setAiAudienceHint"
  | "aiScopeHint"
  | "setAiScopeHint"
  | "aiDocTypeHint"
  | "setAiDocTypeHint"
  | "canManageManufacturerScope"
  | "canManageAppScope"
>;

export function KbAiDetailsGuidanceAndPurpose(props: Props) {
  const {
    aiGuidance,
    aiFeedback,
    aiPurpose,
    setAiPurpose,
    dictation,
    showAiFeedback,
    applyDictationToPurpose,
    aiSourceKind,
    setAiSourceKind,
    aiAudienceHint,
    setAiAudienceHint,
    aiScopeHint,
    setAiScopeHint,
    aiDocTypeHint,
    setAiDocTypeHint,
    canManageManufacturerScope,
    canManageAppScope,
  } = props;

  return (
    <>
      <div className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-xs text-gray-700 space-y-1 dark:border-amber-800 dark:bg-zinc-900/50 dark:text-zinc-300">
        {aiGuidance.map((item) => (
          <div key={item}>{item}</div>
        ))}
      </div>

      {aiFeedback ? (
        <div
          className={
            "rounded-xl border px-3 py-3 text-sm " +
            (aiFeedback.tone === "error"
              ? "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100"
              : aiFeedback.tone === "success"
                ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
                : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100")
          }
        >
          <div className="font-semibold">{aiFeedback.title}</div>
          <div className="mt-1">{aiFeedback.text}</div>
        </div>
      ) : null}

      <div>
        <label className="mb-1 block text-xs text-gray-700 dark:text-zinc-300">Document purpose / AI prompt</label>
        <div className="flex gap-2 items-start">
          <textarea
            className="h-20 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
            value={aiPurpose}
            onChange={(event) => setAiPurpose(event.target.value)}
            placeholder="Describe the document goal, or speak it into the mic and let AI infer the rest."
          />
          <div className="flex shrink-0 flex-col gap-1.5">
            <button
              type="button"
              className={`rounded-lg px-3 py-2 text-sm font-medium ${!dictation.isSupported ? "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-800 dark:text-zinc-400" : dictation.isListening ? "bg-red-600 text-white" : "bg-gray-900 text-white"}`}
              disabled={!dictation.isSupported}
              title={dictation.isListening ? "Stop recording" : "Start recording"}
              onClick={() => {
                showAiFeedback(null);
                dictation.isListening ? dictation.stop() : dictation.start();
              }}
            >
              {dictation.isListening ? "Stop" : "Mic"}
            </button>
            <button
              type="button"
              className={`rounded-lg px-3 py-2 text-sm font-medium ${dictation.draft.trim() ? "bg-white border border-gray-300 text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100" : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-800 dark:text-zinc-400"}`}
              disabled={!dictation.draft.trim()}
              onClick={applyDictationToPurpose}
            >
              Use
            </button>
          </div>
        </div>
        {!dictation.isSupported ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-zinc-400">Microphone dictation is not supported in this browser.</div>
        ) : null}
        {dictation.error ? (
          <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900">
            Dictation error: {dictation.error}
          </div>
        ) : null}
        {dictation.draft.trim() ? (
          <div className="mt-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-300">
            <div className="font-semibold text-gray-900 dark:text-zinc-100">Heard</div>
            <div className="mt-1 whitespace-pre-wrap">{dictation.draft}</div>
          </div>
        ) : null}
      </div>

      <details className="rounded-lg border border-amber-200 bg-white dark:border-amber-800 dark:bg-zinc-900/50">
        <summary className="cursor-pointer list-none px-3 py-2 text-xs text-gray-600 select-none dark:text-zinc-300">
          AI settings&nbsp;·&nbsp;{aiSourceKind}&nbsp;·&nbsp;audience: {aiAudienceHint || "auto"}&nbsp;·&nbsp;scope: {aiScopeHint || "auto"}
          {aiDocTypeHint ? `\u00a0·\u00a0type: ${aiDocTypeHint}` : ""}
        </summary>
        <div className="border-t border-amber-100 px-3 py-3 dark:border-amber-900/50">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Source type</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                value={aiSourceKind}
                onChange={(event) => setAiSourceKind(event.target.value as any)}
              >
                <option value="body">pasted body</option>
                <option value="notes">rough notes</option>
                <option value="policy">policy idea</option>
                <option value="process">process document</option>
                <option value="product">product info</option>
                <option value="training">training material</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Audience hint</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                value={aiAudienceHint}
                onChange={(event) => setAiAudienceHint(event.target.value as any)}
              >
                <option value="">auto</option>
                <option value="internal">internal</option>
                <option value="customer">customer</option>
                <option value="both">both</option>
                <option value="any">any</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Scope hint</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                value={aiScopeHint}
                onChange={(event) => setAiScopeHint(event.target.value as any)}
              >
                <option value="">auto</option>
                <option value="org">org</option>
                <option value="manufacturer" disabled={!canManageManufacturerScope}>
                  manufacturer
                </option>
                <option value="app" disabled={!canManageAppScope}>
                  app
                </option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Type hint</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                value={aiDocTypeHint}
                onChange={(event) => setAiDocTypeHint(event.target.value as any)}
              >
                <option value="">auto</option>
                {DOC_TYPES.map((docType) => (
                  <option key={docType} value={docType}>
                    {docType}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </details>
    </>
  );
}
